<template>
  <div class="summary-message-actions">
    <div class="thumbs-button">
      <template v-if="canProgrammaticallyCopySummary">
        <AppTooltip>
          <template #activator="{ props }">
            <app-button
              v-if="!hasCopied"
              data-locator="copy-summary-button"
              class="actions-button-32"
              v-bind="props"
              type="ghost"
              @click="copySummary"
            >
              <template #left-icon>
                <app-material-icon name="content_copy" />
              </template>
            </app-button>
            <app-button
              v-else
              v-bind="props"
              class="actions-button-32"
              style="background-color: #E7F9E9;"
            >
              <template #left-icon>
                <app-material-icon name="check" color="#068925" />
              </template>
            </app-button>
          </template>
          {{ copyButtonText }}
        </AppTooltip>

        <v-divider class="my-auto" vertical style="height: 24px;" />
      </template>

      <FeedbackPopover
        :disabled="isSummaryUpvoted"
        :tooltip-text="$t('project.documents.ai-summary.DocumentsAISummaryMessageActions.feedbackThumbsUpTooltip')"
        location="top center"
      >
        <template #icon>
          <app-material-icon name="check_circle" color="#2AAD3D" />
        </template>
        <template #trigger>
          <app-button
            data-locator="positive-feedback-button"
            :class="{ 'thumb-up-button': true, active: isSummaryUpvoted }"
            class="actions-button-32"
            type="ghost"
            @click="isSummaryUpvoted ? null : upVote()"
          >
            <template #left-icon>
              <app-material-icon name="thumb_up" :filled="isSummaryUpvoted" />
            </template>
          </app-button>
        </template>
        {{ $t('project.documents.ai-summary.DocumentsAISummaryMessageActions.feedbackThumbsUpPopover') }}
      </FeedbackPopover>

      <AppTooltip :disabled="isSummaryDownvoted">
        <template #activator="{ props }">
          <app-button
            data-locator="negative-feedback-button"
            :class="{'thumb-down-button': true, 'active': isSummaryDownvoted}"
            class="actions-button-32"
            v-bind="props"
            type="ghost"
            @click="isSummaryDownvoted ? null : downVote()"
          >
            <template #left-icon>
              <app-material-icon name="thumb_down" :filled="isSummaryDownvoted" />
            </template>
          </app-button>
        </template>
        {{ $t('project.documents.ai-summary.DocumentsAISummaryMessageActions.feedbackThumbsDownTooltip') }}
      </AppTooltip>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'

import AppTooltip from '@/common/AppTooltip.vue'
import FeedbackPopover from '@/common/popover/FeedbackPopover.vue'
import { SummaryFeedback } from '@/project/documents/pane/DocumentsAISummaryPane.vue'

export default defineComponent({
  name: 'DocumentsAISummaryMessageActions',
  components: { AppTooltip, FeedbackPopover },
  emits: ['copy', 'upvote', 'downvote'],
  props: {
    summaryFeedback: {
      type: Number as PropType<SummaryFeedback>,
      default: () => SummaryFeedback.FEEDBACK_NONE,
    },
  },
  data () {
    return {
      hasCopied: false,
      copyButtonText: this.$t('common.copy'),
    }
  },
  computed: {
    canProgrammaticallyCopySummary () {
      return navigator.clipboard
    },
    isSummaryUpvoted () {
      return this.summaryFeedback === SummaryFeedback.FEEDBACK_POSITIVE
    },
    isSummaryDownvoted () {
      return this.summaryFeedback === SummaryFeedback.FEEDBACK_NEGATIVE
    },
  },
  methods: {
    async copySummary () {
      this.$emit('copy')
      this.hasCopied = true
      this.copyButtonText = `${this.$t('common.copied')} !`

      setTimeout(() => {
        this.hasCopied = false
        this.copyButtonText = this.$t('common.copy')
      }, 3000)
    },
    async upVote () {
      this.$emit('upvote')
    },
    async downVote () {
      this.$emit('downvote')
    },
  },
})
</script>

<style scoped lang="scss">
.summary-message-actions {
  display: flex;
  padding-top: 16px;
  justify-content: space-between;

  .thumbs-button {
    display: flex;
    column-gap: 4px;

    .thumb-up-button, .thumb-down-button {
      padding-inline: 8px;

      &.active {
        background-color: rgb(var(--v-theme-grey-lighten-5));
        cursor: default;
      }
    }
  }

  .actions-button-32 {
    min-height: 32px;
    min-width: 32px;
    height: 32px;
    width: 32px;
    padding: 0;
  }
}
</style>
